// Main modules export file for Relationship Compass
import { relationshipModule01Content } from './modules/relationship-module01-appreciationconnection';
import { relationshipModule02Content } from './modules/relationship-module02-understandingemotions';
import { relationshipModule03Content } from './modules/relationship-module03-communication';
import { relationshipModule04Content } from './modules/relationship-module04-conflict';
import { relationshipModule05Content } from './modules/relationship-module05-intimacy';
import { relationshipModule06Content } from './modules/relationship-module06-trust';
import { relationshipModule07Content } from './modules/relationship-module07-growingtogether';
import { relationshipModule08Content } from './modules/relationship-module08-resilience';

// Module metadata and organization - Relationship Skills Curriculum
export const modulesByLevel = {
  foundation: [
    {
      id: 'appreciation-connection',
      ...relationshipModule01Content,
      difficulty: 'Foundation',
      order: 1,
      prerequisites: [],
      heroImage: '/images/mod01-foundations.png',
      category: 'Foundation'
    },
    {
      id: 'understanding-emotions',
      ...relationshipModule02Content,
      difficulty: 'Foundation',
      order: 2,
      prerequisites: [],
      heroImage: '/images/mod02-emotions.png',
      category: 'Foundation'
    },
    {
      id: 'communication-foundations',
      ...relationshipModule03Content,
      difficulty: 'Foundation',
      order: 3,
      prerequisites: [],
      heroImage: '/images/mod03-communication.png',
      category: 'Foundation'
    }
  ],
  essential: [
    {
      id: 'navigating-conflict',
      ...relationshipModule04Content,
      difficulty: 'Essential',
      order: 4,
      prerequisites: ['appreciation-connection', 'understanding-emotions', 'communication-foundations'],
      heroImage: '/images/mod04-conflict.png',
      category: 'Essential'
    },
    {
      id: 'deepening-intimacy',
      ...relationshipModule05Content,
      difficulty: 'Essential',
      order: 5,
      prerequisites: ['appreciation-connection', 'understanding-emotions'],
      heroImage: '/images/mod05-intimacy.png',
      category: 'Essential'
    },
    {
      id: 'building-trust',
      ...relationshipModule06Content,
      difficulty: 'Essential',
      order: 6,
      prerequisites: ['appreciation-connection', 'understanding-emotions', 'communication-foundations'],
      heroImage: '/images/mod06-trust.png',
      category: 'Essential'
    }
  ],
  advanced: [
    {
      id: 'growing-together',
      ...relationshipModule07Content,
      difficulty: 'Advanced',
      order: 7,
      prerequisites: ['navigating-conflict', 'deepening-intimacy', 'building-trust'],
      heroImage: '/images/mod07-growing.png',
      category: 'Advanced'
    },
    {
      id: 'relationship-resilience',
      ...relationshipModule08Content,
      difficulty: 'Advanced',
      order: 8,
      prerequisites: ['navigating-conflict', 'building-trust'],
      heroImage: '/images/mod08-resilience.png',
      category: 'Advanced'
    }
  ]
};

// Get all modules as flat array
export const getAllModules = () => {
  return Object.values(modulesByLevel).flat();
};

// Get modules by difficulty level
export const getModulesByDifficulty = (difficulty) => {
  return modulesByLevel[difficulty.toLowerCase()] || [];
};

// Get available modules based on completed modules
export const getAvailableModules = (completedModuleIds = []) => {
  const allModules = getAllModules();

  return allModules.filter(module => {
    // Foundation modules are always available
    if (module.difficulty === 'Foundation') return true;

    // Check if all prerequisites are completed
    if (!module.prerequisites || module.prerequisites.length === 0) return true;

    return module.prerequisites.every(prereqId =>
      completedModuleIds.includes(prereqId)
    );
  });
};

// Get next recommended module based on progress
export const getNextRecommendedModule = (completedModuleIds = []) => {
  const availableModules = getAvailableModules(completedModuleIds);
  const incompleteModules = availableModules.filter(
    module => !completedModuleIds.includes(module.id)
  );

  // Return the lowest order incomplete module
  return incompleteModules.sort((a, b) => a.order - b.order)[0] || null;
};

// Get module by ID
export const getModuleById = (moduleId) => {
  return getAllModules().find(module => module.id === moduleId);
};

// Get module progress percentage
export const getModuleProgress = (moduleId, userProgress = {}) => {
  const moduleData = userProgress[moduleId];
  if (!moduleData) return 0;

  return moduleData.progressPercentage || 0;
};

// Check if module is locked
export const isModuleLocked = (moduleId, completedModuleIds = []) => {
  const module = getModuleById(moduleId);
  if (!module) return true;

  // Foundation modules are never locked
  if (module.difficulty === 'Foundation') return false;

  // Check prerequisites
  if (!module.prerequisites || module.prerequisites.length === 0) return false;

  return !module.prerequisites.every(prereqId =>
    completedModuleIds.includes(prereqId)
  );
};

// Get module statistics
export const getModuleStats = () => {
  const allModules = getAllModules();
  return {
    total: allModules.length,
    foundation: modulesByLevel.foundation.length,
    essential: modulesByLevel.essential.length,
    advanced: modulesByLevel.advanced.length
  };
};

export default {
  modulesByLevel,
  getAllModules,
  getModulesByDifficulty,
  getAvailableModules,
  getNextRecommendedModule,
  getModuleById,
  getModuleProgress,
  isModuleLocked,
  getModuleStats
};
